// The globe: at world zoom the flat map curls into a sphere you can orbit.
//
//   flat world band ──ortho render──► texture ──► curl mesh (flat sheet ⇄ sphere, one morph amount)
//   (real scene, layer 0)             (RTT)       (layer 1, unlit)  + glow shell (layer 1)
//
// The map is drawn once, top-down, by the real scene (terrain, ocean, borders, lens, modules), so
// nothing in it knows about the globe and no material is rewritten. The main camera then swaps
// from layer 0 (the scene) to layer 1 (the curl mesh) as soon as the morph starts. The texture is
// redrawn when the data changes and at a slow rate for moving water.
//
//   curl mesh vertex, per (lon offset d from the target, lat φ), target latitude φ0:
//     flat   (x0 + d·K, groundY, z(φ))
//     sphere centre c = (x0, groundY − R, z0):  c + R · (east, up, south) of
//        east  =  cosφ · sin d
//        north =  cosφ0 · sinφ − sinφ0 · cosφ · cos d
//        up    =  sinφ0 · sinφ + cosφ0 · cosφ · cos d        (see globe-math.ts)
//     position = mix(flat, sphere, amount)
//
// The mesh is centred on the camera longitude, so its open seam sits on the far side of the sphere.

import * as THREE from 'three/webgpu';
import {
  Fn, cameraPosition, cos, dFdx, dFdy, dot, float, length, max, mix, normalize, positionGeometry, positionWorld, select, sin, smoothstep,
  texture, uniform, varying, vec2, vec3, vec4, attribute,
} from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import { HEX_COUNT, K, WORLD_H, WORLD_W, hexCol, hexLonLat, hexRow, lonLatToWorld } from '../core/grid.ts';
import { CAMERA, GLOBE, type CivCamera } from './camera.ts';
import type { Engine, QualityTier } from './engine.ts';
import { LIGHT_LOOK } from './lighting.ts';
import type { TerrainChunks } from './terrain/chunks.ts';
import { rgb } from './tsl/color.ts';

type F = Node<'float'>;
type V3 = Node<'vec3'>;

export const GLOBE_LAYER = 1;

const DEG = Math.PI / 180;

/** Texture width by quality tier (about one texel per screen pixel on the globe at 1x). */
const TEXTURE_WIDTH: Readonly<Record<QualityTier, number>> = { low: 2048, med: 3072, high: 4096 };
const ANISOTROPY = 8;

/** The flat map is drawn from this far above the ground, with the height uniform of this view. */
const BAKE_CAMERA_Y = 400;
const BAKE_FAR = 1000;
const BAKE_HEIGHT = GLOBE.from;

/** Redraw limits: after a data change no sooner than MIN, otherwise every REFRESH (moving water). */
const REDRAW_MIN_S = 0.05;
const REDRAW_EVERY_S = 0.5;

/** Below this morph amount the flat scene is drawn as it is (the curl mesh would only add a pop). */
const MORPH_EPS = 0.002;

/** Longest u step (share of the texture width) the sampler may filter over: keeps the pole from averaging the whole map. */
const MAX_DU = 0.008;

/** The outermost texture rows hold the map's dark edge line; the poles sample just inside it. */
const EDGE_V = 0.006;

/**
 * Mid-morph the sheet's far columns and polar rows are still flat, and stand out from the sphere
 * as a barrel with a flat top edge and splayed ice. They fade out over the morph: SIDE_FADE by the
 * longitude offset from the centre, POLAR_FADE by latitude (degrees). FADE_IN and FADE_OUT are the
 * morph amounts where the fade starts to bite and where it is gone again (a whole sphere).
 */
const SIDE_FADE = [90, 140] as const;
const POLAR_FADE = [55, 85] as const;
const FADE_IN = [0.05, 0.35] as const;
const FADE_OUT = [0.8, 1] as const;

const SEGMENTS_LON = 192;
const SEGMENTS_LAT = 96;

// Look. Sun shading keeps relief in the round; the halo is the atmosphere seen edge-on.
const SHADE = [0.7, 1.12] as const;
const SHADE_SUN = [-0.1, 0.95] as const;
const SHELL_SCALE = 1.22;
const RIM_POWER = 3;
const RIM_STRENGTH = 0.3;
const GLOW = { inner: '#5c93d6', outer: '#7db6ff', rimFrom: 0.86, falloff: 26, strength: 0.85, inside: 0.4 } as const;
const GLOW_APPEARS = [0.7, 1] as const;

/** Where a hex sits on screen, on the globe. */
export interface GlobeScreen {
  x: number;
  y: number;
  visible: boolean;
}

export interface Globe {
  /** Per frame after the camera moved. `amount` is the morph, 0 flat to 1 globe. */
  update(amount: number, time: number): void;
  /** The map changed: redraw the texture soon. */
  invalidate(): void;
  /**
   * Compile the bake's pipelines (single-target, unlike the scene pass's) for what is visible now.
   * Whatever is hidden at warm-up compiles on the first bake instead: the sea veil, once a map
   * shows fog, cost ~300 ms there. Call it inside engine.compileAsync (the loop must be paused).
   */
  warm(): Promise<void>;
  /** Screen position of a hex on the globe (hidden on the far side). */
  project(hex: number): GlobeScreen;
  setTier(tier: QualityTier): void;
  dispose(): void;
}

export interface GlobeDeps {
  engine: Engine;
  camera: CivCamera;
  terrain: TerrainChunks;
  /** The terrain shaders' camera height (hex radii); set to the flat world view while baking. */
  camHeight: { value: number };
}

/** A flat sheet of (lon offset, lat) vertices; `flat` holds x offset and z, `lat` the latitude. */
function curlGeometry(): THREE.BufferGeometry {
  const cols = SEGMENTS_LON;
  const rows = SEGMENTS_LAT;
  const count = (cols + 1) * (rows + 1);
  const flat = new Float32Array(count * 3);
  const lat = new Float32Array(count);
  for (let j = 0; j <= rows; j++) {
    const latDeg = 90 - (180 * j) / rows;
    const z = lonLatToWorld(0, latDeg).z;
    for (let i = 0; i <= cols; i++) {
      const k = j * (cols + 1) + i;
      flat.set([(i / cols - 0.5) * WORLD_W, 0, z], k * 3);
      lat[k] = latDeg * DEG;
    }
  }

  // Wound so the flat sheet faces up (+y) and the sphere faces out.
  const index = new Uint32Array(cols * rows * 6);
  let n = 0;
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      const a = j * (cols + 1) + i;
      const b = a + 1;
      const c = a + cols + 1;
      const d = c + 1;
      index.set([a, c, b, b, c, d], n);
      n += 6;
    }
  }

  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(flat, 3));
  g.setAttribute('lat', new THREE.BufferAttribute(lat, 1));
  g.setIndex(new THREE.BufferAttribute(index, 1));
  return g;
}

export function createGlobe(deps: GlobeDeps): Globe {
  const { engine, camera, terrain, camHeight } = deps;
  const scene = engine.scene;
  const renderer = engine.renderer;

  // The bake: the whole flat map from straight above, north up.
  const bakeCamera = new THREE.OrthographicCamera(-WORLD_W / 2, WORLD_W / 2, WORLD_H / 2, -WORLD_H / 2, 1, BAKE_FAR);
  bakeCamera.position.set(WORLD_W / 2, BAKE_CAMERA_Y, WORLD_H / 2);
  bakeCamera.up.set(0, 0, -1);
  bakeCamera.lookAt(WORLD_W / 2, 0, WORLD_H / 2);
  bakeCamera.updateMatrixWorld();

  let target = makeTarget(TEXTURE_WIDTH[engine.tier]);
  const map = texture(target.texture);

  // Uniforms of the morph.
  const amount = uniform(0);
  const centreX = uniform(0);
  const centreZ = uniform(0);
  const sinLat0 = uniform(0);
  const cosLat0 = uniform(1);
  const centreY = CAMERA.groundY - GLOBE.radius;

  const lat = attribute('lat', 'float') as unknown as F;
  const d = (positionGeometry.x as unknown as F).div(K);
  const sinLat = sin(lat);
  const cosLat = cos(lat);
  const east = cosLat.mul(sin(d));
  const north = cosLat0.mul(sinLat).sub(sinLat0.mul(cosLat).mul(cos(d)));
  const up = sinLat0.mul(sinLat).add(cosLat0.mul(cosLat).mul(cos(d)));
  // Unit sphere point in world axes (east, up, south).
  const unitWorld = vec3(east, up, north.negate()) as unknown as V3;

  const flatPos = vec3(centreX.add(positionGeometry.x), CAMERA.groundY, positionGeometry.z);
  const spherePos = vec3(centreX, centreY, centreZ).add(unitWorld.mul(GLOBE.radius));

  const material = new THREE.MeshBasicNodeMaterial({ fog: false });
  material.positionNode = mix(flatPos, spherePos, amount);
  // Render targets are read top row first: north (z = 0) is v = 0.
  const flatV = (positionGeometry.z as unknown as F).div(WORLD_H).clamp(EDGE_V, 1 - EDGE_V);
  const flatUv = vec2(centreX.add(positionGeometry.x).div(WORLD_W), flatV);
  const uv = varying(flatUv, 'globeUv');
  const normalW = varying(mix(vec3(0, 1, 0), unitWorld, amount), 'globeNormal') as unknown as V3;

  // Opaque on the flat map and on the whole sphere; the parts that have not curled yet fade between.
  const unfolded = max(
    smoothstep(SIDE_FADE[0], SIDE_FADE[1], d.abs().div(DEG)),
    smoothstep(POLAR_FADE[0], POLAR_FADE[1], lat.abs().div(DEG)),
  );
  const midMorph = smoothstep(FADE_IN[0], FADE_IN[1], amount).mul(smoothstep(FADE_OUT[0], FADE_OUT[1], amount).oneMinus());
  material.transparent = true;
  material.opacityNode = varying(unfolded.mul(midMorph).oneMinus(), 'globeOpacity') as unknown as F;

  const sun = normalize(vec3(...LIGHT_LOOK.sunDir));
  material.colorNode = Fn(() => {
    const n = normalize(normalW);
    const view = normalize(cameraPosition.sub(positionWorld));
    const sunlit = smoothstep(SHADE_SUN[0], SHADE_SUN[1], dot(n, sun));
    const shade = mix(float(1), mix(float(SHADE[0]), float(SHADE[1]), sunlit), amount);
    const rim = float(1).sub(dot(n, view).clamp(0, 1)).pow(RIM_POWER).mul(amount).mul(RIM_STRENGTH);
    const dx = dFdx(uv) as unknown as Node<'vec2'>;
    const dy = dFdy(uv) as unknown as Node<'vec2'>;
    const gx = vec2(dx.x.clamp(-MAX_DU, MAX_DU), dx.y);
    const gy = vec2(dy.x.clamp(-MAX_DU, MAX_DU), dy.y);
    const base = (map.sample(uv).grad(gx, gy) as unknown as Node<'vec4'>).rgb.mul(shade);
    return vec4(base.add(rgb(GLOW.outer).mul(rim)), 1);
  })();

  const mesh = new THREE.Mesh(curlGeometry(), material);
  mesh.name = 'globe';
  mesh.frustumCulled = false;
  mesh.layers.set(GLOBE_LAYER);
  mesh.visible = false;

  // The halo: a transparent shell around the sphere, shaded by how close each view ray passes to
  // the sphere's centre (the "impact parameter" b), so it reaches past the sphere's outline.
  const shellMat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, fog: false });
  const glowFade = uniform(0);
  const toCentre = vec3(centreX, centreY, centreZ).sub(cameraPosition);
  const ray = normalize(positionWorld.sub(cameraPosition));
  const impact = length(toCentre.sub(ray.mul(dot(toCentre, ray)))).div(GLOBE.radius) as unknown as F;
  const outside = impact.sub(1).mul(GLOW.falloff).negate().exp().mul(GLOW.strength);
  const inside = smoothstep(GLOW.rimFrom, 1.0, impact).mul(GLOW.inside);
  shellMat.colorNode = mix(rgb(GLOW.inner), rgb(GLOW.outer), impact.sub(GLOW.rimFrom).clamp(0, 1));
  shellMat.opacityNode = select(impact.lessThan(1), inside, outside).mul(glowFade);
  const shell = new THREE.Mesh(new THREE.SphereGeometry(GLOBE.radius * SHELL_SCALE, 64, 32), shellMat);
  shell.name = 'globe-glow';
  shell.frustumCulled = false;
  shell.layers.set(GLOBE_LAYER);
  shell.renderOrder = 5;
  shell.visible = false;
  scene.add(mesh, shell);

  let dirty = true;
  let lastBake = -Infinity;
  let baked = false;
  const v = new THREE.Vector3();

  /** Render the flat map into the target. False while the world mesh is not built. */
  const draw = (): boolean => {
    const wasHeight = camHeight.value;
    camHeight.value = BAKE_HEIGHT;
    const previous = renderer.getRenderTarget();
    const drawn = terrain.withWorld(() => {
      renderer.setRenderTarget(target);
      renderer.render(scene, bakeCamera);
    });
    renderer.setRenderTarget(previous);
    camHeight.value = wasHeight;
    return drawn;
  };

  const bake = (time: number): void => {
    // Without the world mesh the texture would show ocean only: stay flat until it exists.
    if (!draw()) {
      return;
    }
    dirty = false;
    baked = true;
    lastBake = time;
  };

  return {
    update(morph, time) {
      const wanted = morph > MORPH_EPS;

      // Redraw the map when stale, or slowly for moving water.
      const age = time - lastBake;
      if (wanted && (!baked || (dirty && age >= REDRAW_MIN_S) || age >= REDRAW_EVERY_S)) {
        bake(time);
      }

      const on = wanted && baked;
      mesh.visible = on;
      shell.visible = on;
      camera.camera.layers.set(on ? GLOBE_LAYER : 0);
      if (!on) {
        return;
      }

      const pose = camera.pose();
      shell.position.set(pose.x, centreY, pose.z);
      const c = camera.centre();
      amount.value = morph;
      centreX.value = pose.x;
      centreZ.value = pose.z;
      sinLat0.value = Math.sin(c.lat);
      cosLat0.value = Math.cos(c.lat);
      glowFade.value = smoothstep01(GLOW_APPEARS[0], GLOW_APPEARS[1], morph);
    },

    invalidate() {
      dirty = true;
    },

    async warm() {
      const previous = renderer.getRenderTarget();
      renderer.setRenderTarget(target);
      try {
        await renderer.compileAsync(scene, bakeCamera);
      } finally {
        renderer.setRenderTarget(previous);
      }

      // One real draw: compileAsync's render objects sit in another render context than render()'s
      // (engine.ts compileAsync), so the first bake would rebuild every shader in JS.
      draw();
    },

    project(hex) {
      if (hex < 0 || hex >= HEX_COUNT || amount.value < GLOBE.grabFrom) {
        return { x: 0, y: 0, visible: false };
      }
      const ll = hexLonLat(hexCol(hex), hexRow(hex));
      const c = camera.centre();
      const dLon = ll.lon * DEG - c.lon;
      const latR = ll.lat * DEG;
      const east = Math.cos(latR) * Math.sin(dLon);
      const north = Math.cos(c.lat) * Math.sin(latR) - Math.sin(c.lat) * Math.cos(latR) * Math.cos(dLon);
      const upY = Math.sin(c.lat) * Math.sin(latR) + Math.cos(c.lat) * Math.cos(latR) * Math.cos(dLon);
      const pose = camera.pose();
      v.set(pose.x + east * GLOBE.radius, CAMERA.groundY - GLOBE.radius + upY * GLOBE.radius, pose.z - north * GLOBE.radius);
      const toCamera = camera.camera.position.clone().sub(v);
      const facing = toCamera.dot(v.clone().sub(new THREE.Vector3(pose.x, centreY, pose.z))) > 0;
      v.project(camera.camera);
      const w = engine.container.clientWidth;
      const h = engine.container.clientHeight;
      return { x: (v.x * 0.5 + 0.5) * w, y: (-v.y * 0.5 + 0.5) * h, visible: facing && Math.abs(v.x) <= 1 && Math.abs(v.y) <= 1 };
    },

    setTier(tier) {
      const width = TEXTURE_WIDTH[tier];
      if (width === target.width) {
        return;
      }
      target.dispose();
      target = makeTarget(width);
      map.value = target.texture;
      dirty = true;
    },

    dispose() {
      mesh.removeFromParent();
      shell.removeFromParent();
      mesh.geometry.dispose();
      shell.geometry.dispose();
      material.dispose();
      shellMat.dispose();
      target.dispose();
      camera.camera.layers.set(0);
    },
  };
}

/** Half-float target: the scene's linear values before tone mapping, mipmapped for the far side. */
function makeTarget(width: number): THREE.RenderTarget {
  const height = Math.round((width * WORLD_H) / WORLD_W);
  const t = new THREE.RenderTarget(width, height, {
    type: THREE.HalfFloatType,
    depthBuffer: true,
    generateMipmaps: true,
    minFilter: THREE.LinearMipmapLinearFilter,
    magFilter: THREE.LinearFilter,
    anisotropy: ANISOTROPY,
  });
  t.texture.wrapS = THREE.RepeatWrapping;
  return t;
}

function smoothstep01(e0: number, e1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}
